import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    Descriptions,
    Drawer,
    Flex,
    Form,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import {
    EyeOutlined,
    PlusOutlined,
    ReloadOutlined,
    RetweetOutlined,
} from '@ant-design/icons';
import {
    createPromotionCampaign,
    createPromotionCode,
    deletePromotionCampaign,
    deletePromotionCode,
    getPromotionCampaign,
    getPromotionCampaigns,
    getPromotionCode,
    getPromotionCodes,
    retryPromotionProvisioning,
    updatePromotionCampaign,
    updatePromotionCode,
} from '../../api/system';

const campaignStatusOptions = [
    { value: 0, label: 'Draft', color: 'default' },
    { value: 1, label: 'Active', color: 'green' },
    { value: 2, label: 'Paused', color: 'orange' },
    { value: 3, label: 'Archived', color: 'red' },
];

const codeStatusOptions = [
    { value: 1, label: 'Active', color: 'green' },
    { value: 2, label: 'Paused', color: 'orange' },
    { value: 3, label: 'Archived', color: 'red' },
];

const providerOptions = [
    { value: 'PROMOTION_PROVIDER_STRIPE', label: 'Stripe' },
    { value: 'PROMOTION_PROVIDER_APPLE', label: 'Apple' },
    { value: 'PROMOTION_PROVIDER_GOOGLE', label: 'Google' },
];

const provisioningOptions = [
    'pending',
    'provisioning',
    'ready',
    'failed',
    'compensating',
    'compensated',
];

const providerLabels = {
    PROMOTION_PROVIDER_STRIPE: 'Stripe',
    PROMOTION_PROVIDER_APPLE: 'Apple',
    PROMOTION_PROVIDER_GOOGLE: 'Google',
    1: 'Stripe',
    2: 'Apple',
    3: 'Google',
};

const provisioningColors = {
    pending: 'default',
    provisioning: 'processing',
    ready: 'success',
    failed: 'error',
    compensating: 'warning',
    compensated: 'default',
};

const compactParams = (params) => Object.fromEntries(
    Object.entries(params).filter(([, value]) => (
        value !== undefined && value !== null && value !== ''
    )),
);

const statusTag = (value, options) => {
    const item = options.find((option) => option.value === Number(value));
    return <Tag color={item?.color}>{item?.label || value}</Tag>;
};

const provisioningTag = (value) => (
    <Tag color={provisioningColors[value] || 'default'}>{value || '-'}</Tag>
);

const providerName = (value) => providerLabels[value] || String(value || '-');

const discountText = (percentOff) => {
    const off = Number(percentOff || 0) / 100;
    const payable = 100 - off;
    return `${off}% off · pay ${payable}%`;
};

const PromotionCodesScreen = () => {
    const [campaignForm] = Form.useForm();
    const [campaignFilterForm] = Form.useForm();
    const [codeForm] = Form.useForm();
    const [codeFilterForm] = Form.useForm();

    const [campaigns, setCampaigns] = useState([]);
    const [campaignLoading, setCampaignLoading] = useState(false);
    const [campaignSubmitting, setCampaignSubmitting] = useState(false);
    const [campaignModalOpen, setCampaignModalOpen] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [campaignPage, setCampaignPage] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [campaignFilters, setCampaignFilters] = useState({});

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [codes, setCodes] = useState([]);
    const [codeLoading, setCodeLoading] = useState(false);
    const [codeSubmitting, setCodeSubmitting] = useState(false);
    const [codeModalOpen, setCodeModalOpen] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [codePage, setCodePage] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [codeFilters, setCodeFilters] = useState({});

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const campaignChoices = useMemo(
        () => campaigns.map((campaign) => ({
            value: campaign.id,
            label: `${campaign.name} (#${campaign.id})`,
        })),
        [campaigns],
    );

    const fetchCampaigns = async (
        current = campaignPage.current,
        pageSize = campaignPage.pageSize,
        filters = campaignFilters,
    ) => {
        setCampaignLoading(true);
        try {
            const result = await getPromotionCampaigns(compactParams({
                'page.num': current,
                'page.size': pageSize,
                ...filters,
            }));
            setCampaigns(result?.list || []);
            setCampaignPage({
                current,
                pageSize,
                total: Number(result?.page?.total || 0),
            });
        } catch (error) {
            message.error('Failed to load promotion campaigns');
        } finally {
            setCampaignLoading(false);
        }
    };

    const fetchCodes = async (
        campaign = selectedCampaign,
        current = codePage.current,
        pageSize = codePage.pageSize,
        filters = codeFilters,
    ) => {
        if (!campaign?.id) {
            setCodes([]);
            setCodePage((old) => ({ ...old, current: 1, total: 0 }));
            return;
        }
        setCodeLoading(true);
        try {
            const result = await getPromotionCodes(compactParams({
                'page.num': current,
                'page.size': pageSize,
                campaignId: campaign.id,
                ...filters,
            }));
            setCodes(result?.list || []);
            setCodePage({
                current,
                pageSize,
                total: Number(result?.page?.total || 0),
            });
        } catch (error) {
            message.error('Failed to load promotion codes');
        } finally {
            setCodeLoading(false);
        }
    };

    useEffect(() => {
        fetchCampaigns(1, 10, {});
    }, []);

    const submitCampaignFilters = (values) => {
        const filters = compactParams(values);
        setCampaignFilters(filters);
        fetchCampaigns(1, campaignPage.pageSize, filters);
    };

    const resetCampaignFilters = () => {
        campaignFilterForm.resetFields();
        setCampaignFilters({});
        fetchCampaigns(1, campaignPage.pageSize, {});
    };

    const openCreateCampaign = () => {
        setEditingCampaign(null);
        campaignForm.setFieldsValue({
            name: '',
            status: 0,
            codeType: 'checkout',
            percentOff: 5000,
            durationMonths: 3,
            providers: [
                'PROMOTION_PROVIDER_STRIPE',
                'PROMOTION_PROVIDER_APPLE',
            ],
        });
        setCampaignModalOpen(true);
    };

    const openEditCampaign = (campaign) => {
        setEditingCampaign(campaign);
        campaignForm.setFieldsValue({
            name: campaign.name,
            status: Number(campaign.status),
            codeType: campaign.codeType,
            percentOff: Number(campaign.percentOff),
            durationMonths: Number(campaign.durationMonths),
            providers: campaign.providers || [],
        });
        setCampaignModalOpen(true);
    };

    const submitCampaign = async () => {
        const values = await campaignForm.validateFields();
        setCampaignSubmitting(true);
        try {
            let result;
            if (editingCampaign) {
                const payload = {
                    id: editingCampaign.id,
                    name: values.name,
                    status: values.status,
                };
                if (editingCampaign.provisioningStatus === 'pending') {
                    payload.percentOff = values.percentOff;
                    payload.durationMonths = values.durationMonths;
                }
                result = await updatePromotionCampaign(payload);
            } else {
                result = await createPromotionCampaign({
                    name: values.name,
                    status: values.status,
                    codeType: values.codeType,
                    percentOff: values.percentOff,
                    durationMonths: values.durationMonths,
                    providers: values.providers,
                });
            }
            if (!result) {
                throw new Error('Empty promotion campaign response');
            }
            message.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
            setCampaignModalOpen(false);
            await fetchCampaigns();
        } catch (error) {
            if (!error?.errorFields) {
                message.error('Unable to save promotion campaign');
            }
        } finally {
            setCampaignSubmitting(false);
        }
    };

    const archiveCampaign = async (campaign) => {
        setCampaignLoading(true);
        try {
            const result = await deletePromotionCampaign(campaign.id);
            if (!result) {
                throw new Error('Empty archive response');
            }
            message.success('Campaign archived');
            if (selectedCampaign?.id === campaign.id) {
                setSelectedCampaign(null);
                setCodes([]);
            }
            await fetchCampaigns();
        } catch (error) {
            message.error('Unable to archive campaign');
        } finally {
            setCampaignLoading(false);
        }
    };

    const retryProvisioning = async (campaign, codeId, provider) => {
        const result = await retryPromotionProvisioning(compactParams({
            campaignId: campaign.id,
            promotionCodeId: codeId,
            provider,
        }));
        if (!result) {
            message.error('Unable to schedule provisioning retry');
            return;
        }
        message.success('Provisioning retry scheduled');
        await fetchCampaigns();
        if (selectedCampaign?.id === campaign.id) {
            await fetchCodes(campaign);
        }
    };

    const manageCodes = (campaign) => {
        setSelectedCampaign(campaign);
        setCodeFilters({});
        codeFilterForm.resetFields();
        fetchCodes(campaign, 1, codePage.pageSize, {});
    };

    const submitCodeFilters = (values) => {
        const filters = compactParams(values);
        setCodeFilters(filters);
        fetchCodes(selectedCampaign, 1, codePage.pageSize, filters);
    };

    const resetCodeFilters = () => {
        codeFilterForm.resetFields();
        setCodeFilters({});
        fetchCodes(selectedCampaign, 1, codePage.pageSize, {});
    };

    const openCreateCode = () => {
        setEditingCode(null);
        codeForm.setFieldsValue({
            campaignId: selectedCampaign?.id,
            promotionCode: '',
            status: 1,
        });
        setCodeModalOpen(true);
    };

    const openEditCode = (code) => {
        setEditingCode(code);
        codeForm.setFieldsValue({
            campaignId: code.campaignId,
            promotionCode: code.promotionCode,
            status: Number(code.status),
        });
        setCodeModalOpen(true);
    };

    const submitCode = async () => {
        const values = await codeForm.validateFields();
        setCodeSubmitting(true);
        try {
            const result = editingCode
                ? await updatePromotionCode({
                    id: editingCode.id,
                    status: values.status,
                })
                : await createPromotionCode({
                    campaignId: values.campaignId,
                    promotionCode: values.promotionCode.trim().toUpperCase(),
                    status: values.status,
                });
            if (!result) {
                throw new Error('Empty promotion code response');
            }
            message.success(editingCode ? 'Code updated' : 'Code created');
            setCodeModalOpen(false);
            await fetchCodes();
        } catch (error) {
            if (!error?.errorFields) {
                message.error('Unable to save promotion code');
            }
        } finally {
            setCodeSubmitting(false);
        }
    };

    const archiveCode = async (code) => {
        setCodeLoading(true);
        try {
            const result = await deletePromotionCode(code.id);
            if (!result) {
                throw new Error('Empty archive response');
            }
            message.success('Code archived');
            await fetchCodes();
        } catch (error) {
            message.error('Unable to archive promotion code');
        } finally {
            setCodeLoading(false);
        }
    };

    const openCampaignDetail = async (campaign) => {
        setDetailLoading(true);
        setDetail({ type: 'campaign', data: campaign });
        try {
            const result = await getPromotionCampaign(campaign.id);
            if (result) {
                setDetail({ type: 'campaign', data: result });
            }
        } catch (error) {
            message.error('Unable to load campaign details');
        } finally {
            setDetailLoading(false);
        }
    };

    const openCodeDetail = async (code) => {
        setDetailLoading(true);
        setDetail({ type: 'code', data: code });
        try {
            const result = await getPromotionCode(code.id);
            if (result) {
                setDetail({ type: 'code', data: result });
            }
        } catch (error) {
            message.error('Unable to load code details');
        } finally {
            setDetailLoading(false);
        }
    };

    const campaignColumns = [
        {
            title: 'Campaign',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space direction='vertical' size={0}>
                    <Typography.Text strong>{name}</Typography.Text>
                    <Typography.Text type='secondary'>#{record.id}</Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Terms',
            key: 'terms',
            render: (_, record) => (
                <Space direction='vertical' size={0}>
                    <span>{discountText(record.percentOff)}</span>
                    <Typography.Text type='secondary'>
                        {record.durationMonths} monthly billing period(s)
                    </Typography.Text>
                </Space>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'codeType',
            key: 'codeType',
            render: (value) => <Tag color={value === 'signup' ? 'purple' : 'blue'}>{value}</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => statusTag(value, campaignStatusOptions),
        },
        {
            title: 'Provisioning',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'Providers',
            dataIndex: 'providers',
            key: 'providers',
            render: (providers = []) => providers.map((provider) => (
                <Tag key={provider}>{providerName(provider)}</Tag>
            )),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 330,
            render: (_, record) => (
                <Space wrap>
                    <Button type='link' icon={<EyeOutlined />} onClick={() => openCampaignDetail(record)}>
                        Details
                    </Button>
                    <Button type='link' onClick={() => manageCodes(record)}>Codes</Button>
                    <Button type='link' onClick={() => openEditCampaign(record)}>Edit</Button>
                    <Button
                        type='link'
                        icon={<RetweetOutlined />}
                        onClick={() => retryProvisioning(record)}
                    >
                        Retry
                    </Button>
                    <Popconfirm
                        title='Archive this campaign?'
                        description='New redemptions will stop. Existing discounted subscriptions are unchanged.'
                        onConfirm={() => archiveCampaign(record)}
                    >
                        <Button type='link' danger disabled={Number(record.status) === 3}>Archive</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const codeColumns = [
        {
            title: 'Code',
            dataIndex: 'promotionCode',
            key: 'promotionCode',
            render: (value) => <Typography.Text copyable strong>{value}</Typography.Text>,
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value) => statusTag(value, codeStatusOptions),
        },
        {
            title: 'Provisioning',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'Provider states',
            dataIndex: 'providerStates',
            key: 'providerStates',
            render: (states = []) => states.map((state) => (
                <Tag
                    key={`${state.provider}-${state.provisioningStatus}`}
                    color={provisioningColors[state.provisioningStatus] || 'default'}
                >
                    {providerName(state.provider)}: {state.provisioningStatus}
                </Tag>
            )),
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 280,
            render: (_, record) => (
                <Space wrap>
                    <Button type='link' icon={<EyeOutlined />} onClick={() => openCodeDetail(record)}>
                        Details
                    </Button>
                    <Button type='link' onClick={() => openEditCode(record)}>Status</Button>
                    <Button
                        type='link'
                        icon={<RetweetOutlined />}
                        onClick={() => retryProvisioning(selectedCampaign, record.id)}
                    >
                        Retry
                    </Button>
                    <Popconfirm
                        title='Archive this promotion code?'
                        onConfirm={() => archiveCode(record)}
                    >
                        <Button type='link' danger disabled={Number(record.status) === 3}>Archive</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const detailData = detail?.data;
    const detailStates = detail?.type === 'campaign'
        ? detailData?.providerStates
        : detailData?.providerStates;

    const providerStateColumns = [
        {
            title: 'Provider',
            dataIndex: 'provider',
            key: 'provider',
            render: providerName,
        },
        {
            title: 'Status',
            dataIndex: 'provisioningStatus',
            key: 'provisioningStatus',
            render: provisioningTag,
        },
        {
            title: 'External resource',
            key: 'externalResource',
            render: (_, record) => (
                <Typography.Text copyable>
                    {record.externalOfferKey || record.externalCodeKey || '-'}
                </Typography.Text>
            ),
        },
        {
            title: 'Transaction key',
            dataIndex: 'transactionOfferKey',
            key: 'transactionOfferKey',
            render: (value) => value ? <Typography.Text copyable>{value}</Typography.Text> : '-',
        },
        {
            title: 'Last verified',
            dataIndex: 'lastVerifiedAt',
            key: 'lastVerifiedAt',
            render: (value) => value || '-',
        },
        {
            title: 'Last error',
            key: 'lastError',
            render: (_, record) => record.lastErrorMessage
                ? <Typography.Text type='danger'>{record.lastErrorCode}: {record.lastErrorMessage}</Typography.Text>
                : '-',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button
                    type='link'
                    icon={<RetweetOutlined />}
                    onClick={() => retryProvisioning(
                        detail?.type === 'campaign' ? detailData : selectedCampaign,
                        detail?.type === 'code' ? detailData?.id : undefined,
                        record.provider,
                    )}
                >
                    Retry
                </Button>
            ),
        },
    ];

    return (
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
            <Card
                title='Promotion Campaigns'
                extra={(
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => fetchCampaigns()}>Refresh</Button>
                        <Button type='primary' icon={<PlusOutlined />} onClick={openCreateCampaign}>
                            New Campaign
                        </Button>
                    </Space>
                )}
            >
                <Form form={campaignFilterForm} layout='inline' onFinish={submitCampaignFilters}>
                    <Form.Item name='keyword' label='Keyword'>
                        <Input allowClear placeholder='Name' />
                    </Form.Item>
                    <Form.Item name='status' label='Status'>
                        <Select allowClear options={campaignStatusOptions} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name='provisioningStatus' label='Provisioning'>
                        <Select
                            allowClear
                            options={provisioningOptions.map((value) => ({ value, label: value }))}
                            style={{ width: 150 }}
                        />
                    </Form.Item>
                    <Form.Item name='codeType' label='Type'>
                        <Select
                            allowClear
                            options={[
                                { value: 'signup', label: 'Signup' },
                                { value: 'checkout', label: 'Checkout' },
                            ]}
                            style={{ width: 120 }}
                        />
                    </Form.Item>
                    <Form.Item name='provider' label='Provider'>
                        <Select allowClear options={providerOptions} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type='primary' htmlType='submit'>Search</Button>
                            <Button onClick={resetCampaignFilters}>Reset</Button>
                        </Space>
                    </Form.Item>
                </Form>
                <Table
                    style={{ marginTop: 20 }}
                    rowKey='id'
                    scroll={{ x: 1200 }}
                    loading={campaignLoading}
                    columns={campaignColumns}
                    dataSource={campaigns}
                    pagination={{
                        current: campaignPage.current,
                        pageSize: campaignPage.pageSize,
                        total: campaignPage.total,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => fetchCampaigns(current, pageSize),
                    }}
                />
            </Card>

            <Card
                title={selectedCampaign
                    ? `Promotion Codes · ${selectedCampaign.name}`
                    : 'Promotion Codes · select a campaign'}
                extra={(
                    <Space>
                        <Select
                            showSearch
                            optionFilterProp='label'
                            placeholder='Select campaign'
                            value={selectedCampaign?.id}
                            options={campaignChoices}
                            style={{ width: 300 }}
                            onChange={(id) => {
                                const campaign = campaigns.find((item) => item.id === id);
                                if (campaign) manageCodes(campaign);
                            }}
                        />
                        <Button
                            icon={<ReloadOutlined />}
                            disabled={!selectedCampaign}
                            onClick={() => fetchCodes()}
                        >
                            Refresh
                        </Button>
                        <Button
                            type='primary'
                            icon={<PlusOutlined />}
                            disabled={!selectedCampaign || selectedCampaign.provisioningStatus !== 'ready'}
                            onClick={openCreateCode}
                        >
                            New Code
                        </Button>
                    </Space>
                )}
            >
                <Form form={codeFilterForm} layout='inline' onFinish={submitCodeFilters}>
                    <Form.Item name='promotionCode' label='Code'>
                        <Input allowClear />
                    </Form.Item>
                    <Form.Item name='status' label='Status'>
                        <Select allowClear options={codeStatusOptions} style={{ width: 120 }} />
                    </Form.Item>
                    <Form.Item name='provisioningStatus' label='Provisioning'>
                        <Select
                            allowClear
                            options={provisioningOptions.map((value) => ({ value, label: value }))}
                            style={{ width: 150 }}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type='primary' htmlType='submit' disabled={!selectedCampaign}>Search</Button>
                            <Button onClick={resetCodeFilters} disabled={!selectedCampaign}>Reset</Button>
                        </Space>
                    </Form.Item>
                </Form>
                <Table
                    style={{ marginTop: 20 }}
                    rowKey='id'
                    scroll={{ x: 1000 }}
                    loading={codeLoading}
                    columns={codeColumns}
                    dataSource={codes}
                    locale={{ emptyText: selectedCampaign ? 'No promotion codes' : 'Select a campaign first' }}
                    pagination={{
                        current: codePage.current,
                        pageSize: codePage.pageSize,
                        total: codePage.total,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => fetchCodes(
                            selectedCampaign,
                            current,
                            pageSize,
                        ),
                    }}
                />
            </Card>

            <Modal
                title={editingCampaign ? 'Edit Promotion Campaign' : 'New Promotion Campaign'}
                open={campaignModalOpen}
                confirmLoading={campaignSubmitting}
                onOk={submitCampaign}
                onCancel={() => setCampaignModalOpen(false)}
                destroyOnClose
                width={680}
            >
                <Form form={campaignForm} layout='vertical' preserve={false}>
                    <Form.Item
                        name='name'
                        label='Campaign name'
                        rules={[{ required: true, whitespace: true }]}
                    >
                        <Input maxLength={100} />
                    </Form.Item>
                    <Flex gap={16}>
                        <Form.Item name='codeType' label='Code type' style={{ flex: 1 }}>
                            <Select
                                disabled={Boolean(editingCampaign)}
                                options={[
                                    { value: 'signup', label: 'Signup — validate before account creation' },
                                    { value: 'checkout', label: 'Checkout — enter on Pro purchase page' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item name='status' label='Business status' style={{ width: 180 }}>
                            <Select
                                options={editingCampaign
                                    ? campaignStatusOptions
                                    : campaignStatusOptions.slice(0, 2)}
                            />
                        </Form.Item>
                    </Flex>
                    <Flex gap={16}>
                        <Form.Item
                            name='percentOff'
                            label='Percent off (basis points)'
                            tooltip='1150 means 11.5% off; the customer pays 88.5%.'
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={10000}
                                disabled={editingCampaign && editingCampaign.provisioningStatus !== 'pending'}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name='durationMonths'
                            label='Discounted monthly periods'
                            rules={[{ required: true }]}
                            style={{ flex: 1 }}
                        >
                            <InputNumber
                                min={1}
                                max={12}
                                disabled={editingCampaign && editingCampaign.provisioningStatus !== 'pending'}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Flex>
                    <Form.Item
                        name='providers'
                        label='Providers'
                        rules={[{ required: true, message: 'Select at least one provider' }]}
                    >
                        <Checkbox.Group disabled={Boolean(editingCampaign)} options={providerOptions} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingCode ? 'Update Promotion Code Status' : 'New Promotion Code'}
                open={codeModalOpen}
                confirmLoading={codeSubmitting}
                onOk={submitCode}
                onCancel={() => setCodeModalOpen(false)}
                destroyOnClose
            >
                <Form form={codeForm} layout='vertical' preserve={false}>
                    <Form.Item name='campaignId' label='Campaign ID'>
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name='promotionCode'
                        label='Promotion code'
                        rules={[
                            { required: true },
                            {
                                pattern: /^[A-Za-z0-9]{1,64}$/,
                                message: 'Use 1–64 letters or numbers',
                            },
                        ]}
                    >
                        <Input
                            disabled={Boolean(editingCode)}
                            maxLength={64}
                            style={{ textTransform: 'uppercase' }}
                        />
                    </Form.Item>
                    <Form.Item name='status' label='Business status' rules={[{ required: true }]}>
                        <Select options={codeStatusOptions} />
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                title={detail?.type === 'campaign' ? 'Campaign details' : 'Promotion code details'}
                open={Boolean(detail)}
                loading={detailLoading}
                onClose={() => setDetail(null)}
                width={900}
            >
                {detailData && (
                    <Space direction='vertical' size='large' style={{ width: '100%' }}>
                        <Descriptions bordered column={2}>
                            <Descriptions.Item label='ID'>{detailData.id}</Descriptions.Item>
                            <Descriptions.Item label='Name / Code'>
                                {detailData.name || detailData.promotionCode}
                            </Descriptions.Item>
                            <Descriptions.Item label='Business status'>
                                {statusTag(
                                    detailData.status,
                                    detail?.type === 'campaign'
                                        ? campaignStatusOptions
                                        : codeStatusOptions,
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label='Provisioning'>
                                {provisioningTag(detailData.provisioningStatus)}
                            </Descriptions.Item>
                            {detail?.type === 'campaign' && (
                                <>
                                    <Descriptions.Item label='Code type'>{detailData.codeType}</Descriptions.Item>
                                    <Descriptions.Item label='Terms'>
                                        {discountText(detailData.percentOff)} for {detailData.durationMonths} month(s)
                                    </Descriptions.Item>
                                </>
                            )}
                            {detail?.type === 'code' && (
                                <Descriptions.Item label='Campaign ID'>
                                    {detailData.campaignId}
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label='Created'>{detailData.createdAt || '-'}</Descriptions.Item>
                            <Descriptions.Item label='Updated'>{detailData.updatedAt || '-'}</Descriptions.Item>
                        </Descriptions>
                        <Table
                            rowKey={(record) => `${record.provider}-${record.externalOfferKey || record.externalCodeKey || 'none'}`}
                            size='small'
                            pagination={false}
                            columns={providerStateColumns}
                            dataSource={detailStates || []}
                            scroll={{ x: 1000 }}
                        />
                    </Space>
                )}
            </Drawer>
        </Space>
    );
};

export default PromotionCodesScreen;
